import type { Activity } from "../api/assignments/Assignments.domain";
import type { Rule, Rules } from "../api/rules/Rules.domain";

function getEffectiveStepCode(rules: Rules, activity: Activity | null): string | undefined {
  if (!activity) return undefined;
  if (activity.responseDetail?.stepCode) return activity.responseDetail.stepCode;

  const rule = rules.rules.find((r) => r.activityTypeCode === activity.activityType?.code);
  if (!rule) return undefined;

  // Only infer when unambiguous (single possibleReturn for the given OK/KO).
  const matches = rule.possibleReturns.filter((pr) => pr.returnStatus === activity.returnStatus);
  if (matches.length === 1) return matches[0].stepCode;
  return undefined;
}

/**
 * Returns whether the next activity in the sequence would be external
 * (so the "Add activity" button should be disabled).
 */
export function isNextActivityExternal(
  rules: Rules | null | undefined,
  activities: Activity[],
  logisticsModeCode: string,
): boolean {
  if (!rules) return true;

  const currentStep = activities.length;
  const lastActivity = currentStep > 0 ? activities[currentStep - 1] : null;

  if (lastActivity?.activityType?.code) {
    const currentRule = rules.rules.find(
      (r) => r.activityTypeCode === lastActivity.activityType?.code,
    );
    if (currentRule?.conditionalRules?.nextActivity) {
      return false;
    }
  }

  const mode = logisticsModeCode || "DEFAULT";
  const nextActivity = rules.rules.find((rule) => {
    if (rule.conditionalRules) return false;
    const logisticsRule = rule.logisticsModeRules?.[mode as keyof typeof rule.logisticsModeRules];
    if (logisticsRule?.forbidden) return false;
    return true;
  });

  return nextActivity?.isExternalActivity === true;
}

/**
 * Returns the rule for the next activity to add (conditional or sequence),
 * or null if none (e.g. next would be external or no rule found).
 */
export function getNextActivityRule(
  rules: Rules,
  activities: Activity[],
  logisticsModeCode: string,
): Rule | null {
  const currentStep = activities.length;
  const lastActivity = currentStep > 0 ? activities[currentStep - 1] : null;
  const lastStepCode = getEffectiveStepCode(rules, lastActivity);
  const mode = logisticsModeCode || "DEFAULT";

  const conditionalActivity = rules.rules.find((rule: Rule): boolean => {
    if (rule.isExternalActivity) return false;
    if (lastActivity?.activityType?.code) {
      const previousRule = rules.rules.find(
        (r) => r.activityTypeCode === lastActivity.activityType?.code,
      );
      if (previousRule?.conditionalRules?.nextActivity) {
        return (
          rule.activityTypeCode === previousRule.conditionalRules.nextActivity &&
          !rule.isExternalActivity
        );
      }
    }
    if (!rule.conditionalRules?.triggers) return false;
    return rule.conditionalRules.triggers.some(
      (trigger) =>
        trigger.previousActivity === lastActivity?.activityType?.code &&
        trigger.whenStatus === lastStepCode,
    );
  });

  if (conditionalActivity) return conditionalActivity;

  const sequence = rules.logisticsModeSequences[mode] || rules.logisticsModeSequences.DEFAULT;
  const nextActivityCode = sequence?.[currentStep];
  if (!nextActivityCode) return null;

  const availableRule = rules.rules.find((rule) => {
    if (rule.conditionalRules || rule.isExternalActivity) return false;
    return rule.activityTypeCode === nextActivityCode;
  });

  return availableRule ?? null;
}
